<template>
    <!-- Forge Notetaker (Keelan, 1 Nov 2026): connect Outlook, and the "Ironbook Notetaker" joins your calls.
         API: routes/ainotetaker.ts. Hidden unless the server has it set up and it is switched on for this account. -->
    <section v-if="state?.available" id="notetaker" class="fnt" aria-labelledby="fnt-title">
        <div class="fnt-head">
            <div>
                <div id="fnt-title" class="fnt-eyebrow">Notetaker</div>
                <h2 class="fnt-title">{{ state.botName }} joins your calls and writes the notes</h2>
            </div>
            <span v-if="state.connected" class="fnt-pill" :class="state.status == 'connected' ? 'fnt-pill-ok' : 'fnt-pill-warn'">{{ state.status == 'connected' ? 'Outlook connected' : 'Reconnect Outlook' }}</span>
        </div>

        <p v-if="message" class="fnt-msg" :class="'fnt-msg-' + message.tone" role="status">{{ message.text }}</p>

        <template v-if="!state.connected">
            <p class="fnt-text">Connect your Outlook calendar and the {{ state.botName }} joins your Teams, Zoom and Google Meet calls about 2 minutes before they start. When the call ends, your notes appear in My AI Captures, attached to the Ironbook meeting when the call is one of yours.</p>
            <ul class="fnt-list">
                <li>It only reads your calendar. It can't change or send anything.</li>
                <li>People in the call see it, and it posts a message saying it's taking notes.</li>
                <li>The call audio is kept {{ state.audioDays }} days, then deleted. Notes stay.</li>
            </ul>
            <a class="fnt-btn" :href="api + '/api/ai/notetaker/connect'">Connect Outlook calendar</a>
        </template>

        <template v-else>
            <p v-if="!state.here" class="fnt-text">Your calendar is connected in another company on Ironbook, so its calls go there.</p>
            <template v-else>
                <p class="fnt-text">Connected as <strong>{{ state.email || 'your Outlook account' }}</strong>.
                    <template v-if="state.status != 'connected'">Microsoft stopped the connection (a password change can do this). Connect again to keep the notetaker joining.</template></p>
                <fieldset class="fnt-rule">
                    <legend>Which calls should it join?</legend>
                    <label v-for="r in rules" :key="r.value" class="fnt-radio">
                        <input :id="'fnt-rule-' + r.value" type="radio" name="fnt-rule" :value="r.value" :checked="state.joinRule == r.value" :disabled="busy" @change="setRule(r.value)">
                        <span>{{ r.label }}</span>
                    </label>
                </fieldset>

                <div class="fnt-sub">Next 14 days</div>
                <p v-if="!state.upcoming.length" class="fnt-muted">No calls with a meeting link on your calendar yet. New invites show up here within a few minutes.</p>
                <div v-for="e in state.upcoming" :key="e.id" class="fnt-row">
                    <div class="fnt-when">{{ day(e.start) }}<span>{{ time(e.start) }}</span></div>
                    <div class="fnt-what">
                        <div class="fnt-name">{{ e.title }}</div>
                        <div class="fnt-meta">{{ platformName(e.platform) }}<template v-if="e.error"> · <span class="fnt-err">{{ e.error }}</span></template></div>
                    </div>
                    <button type="button" class="fnt-toggle" :class="{ on: e.joining }" :aria-pressed="e.joining" :disabled="busy" @click="toggle(e)">
                        {{ e.joining ? 'Notetaker on' : 'Notetaker off' }}
                    </button>
                </div>

                <template v-if="state.recent.length">
                    <div class="fnt-sub">Recent calls</div>
                    <div v-for="e in state.recent" :key="'r' + e.id" class="fnt-row">
                        <div class="fnt-when">{{ day(e.start) }}<span>{{ time(e.start) }}</span></div>
                        <div class="fnt-what">
                            <div class="fnt-name">{{ e.title }}</div>
                            <div class="fnt-meta">{{ recentStatus(e) }}</div>
                        </div>
                        <router-link v-if="e.jobId" class="fnt-open" :to="'/ai-review/' + e.jobId">Open notes</router-link>
                    </div>
                </template>

                <button type="button" class="fnt-link" :disabled="busy" @click="disconnect">Disconnect Outlook</button>
            </template>
        </template>
    </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';

const MESSAGES: Record<string, { text: string; tone: string }> = {
    connected: { text: 'Outlook is connected. The notetaker will join your calls with a meeting link.', tone: 'ok' },
    cancelled: { text: 'Outlook was not connected.', tone: 'info' },
    expired: { text: 'That sign-in took too long or was started in another tab. Try Connect again.', tone: 'warn' },
    'admin-consent': { text: 'Your Microsoft 365 administrator needs to approve the Ironbook Notetaker before you can connect. Ask them to approve it, then try again.', tone: 'warn' },
    'microsoft-error': { text: 'Microsoft did not finish the sign-in. Try Connect again.', tone: 'warn' },
    'other-company': { text: 'Your calendar is already connected in another company on Ironbook. Disconnect it there first.', tone: 'warn' },
    'not-enabled': { text: 'The notetaker is not switched on for your account yet.', tone: 'warn' },
    failed: { text: 'The calendar could not be connected. Please try again.', tone: 'warn' }
};

export default defineComponent({
    name: 'ForgeNotetaker',
    data() {
        return {
            state: null as any,
            busy: false,
            message: null as null | { text: string; tone: string },
            rules: [
                { value: 'all', label: 'Every call with a meeting link' },
                { value: 'organizer', label: 'Only calls I organize' },
                { value: 'manual', label: 'Only calls I switch on below' }
            ]
        };
    },
    computed: {
        api(): string { return this.$store.state.apiUrl || ''; }
    },
    async mounted() {
        const result = String(this.$route.query.notetaker || '');
        if (MESSAGES[result]) {
            this.message = MESSAGES[result];
            const query = { ...this.$route.query }; delete query.notetaker;
            this.$router.replace({ query, hash: this.$route.hash });
        }
        await this.load();
    },
    methods: {
        async load() {
            try { this.state = (await axios.get(`${this.api}/api/ai/notetaker`)).data; } catch { this.state = null; }
        },
        async run(fn: () => Promise<any>, failText: string) {
            this.busy = true;
            try { await fn(); await this.load(); }
            catch (e: any) { this.message = { text: (typeof e?.response?.data === 'string' && e.response.data) || failText, tone: 'warn' }; }
            finally { this.busy = false; }
        },
        setRule(rule: string) {
            this.run(async () => { await axios.put(`${this.api}/api/ai/notetaker/settings`, { joinRule: rule }); this.message = { text: 'Saved. Your upcoming calls were updated.', tone: 'ok' }; }, 'The setting could not be saved. Please try again.');
        },
        toggle(e: any) {
            this.run(() => axios.post(`${this.api}/api/ai/notetaker/events/${e.id}`, { on: !e.joining }), 'That meeting could not be changed. Please try again.');
        },
        disconnect() {
            this.run(async () => { await axios.post(`${this.api}/api/ai/notetaker/disconnect`); this.message = { text: 'Outlook is disconnected. The notetaker will not join any more calls.', tone: 'info' }; }, 'Outlook could not be disconnected. Please try again.');
        },
        day(d: string) { return new Date(d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }); },
        time(d: string) { return new Date(d).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); },
        platformName(p: string) {
            return ({ microsoft_teams: 'Teams', zoom: 'Zoom', google_meet: 'Google Meet', webex: 'Webex', goto_meeting: 'GoTo' } as any)[p] || 'Online meeting';
        },
        recentStatus(e: any) {
            if (e.jobId) return 'Notes are in My AI Captures';
            if (e.ingest === 'pending' || e.ingest === 'running') return 'Bringing the recording into Ironbook…';
            if (e.ingest === 'failed' || e.ingest === 'no-recording' || e.error) return e.error || 'No recording from this call';
            if (e.botStatus === 'in_waiting_room') return 'Waited in the lobby and was not let in';
            if (e.botStatus) return 'The notetaker was in this call';
            return 'Not recorded';
        }
    }
});
</script>

<style scoped>
.fnt { margin-top: 22px; background: #fff; border: 1px solid #f0e3d6; border-radius: 12px; padding: 18px 20px; color: #4b4138; font-size: 14px; }
.fnt-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.fnt-eyebrow { font-weight: 700; text-transform: uppercase; font-size: 11.5px; letter-spacing: .08em; color: #a0958a; }
.fnt-title { margin: 4px 0 8px; font-size: 18px; font-weight: 800; color: #1f1712; text-wrap: balance; }
.fnt-pill { font-size: 11.5px; font-weight: 700; border-radius: 12px; padding: 3px 10px; white-space: nowrap; }
.fnt-pill-ok { background: #eafaf0; color: #1b7a3d; border: 1px solid #b7e4c7; }
.fnt-pill-warn { background: #fff8e6; color: #8a6d00; border: 1px solid #ffe08a; }
.fnt-text { margin: 0 0 10px; line-height: 1.55; max-width: 68ch; }
.fnt-list { margin: 0 0 14px; padding-left: 18px; line-height: 1.6; }
.fnt-btn { display: inline-block; background: #a64010; color: #fff; font-weight: 700; border-radius: 8px; padding: 9px 16px; text-decoration: none; }
.fnt-btn:hover, .fnt-btn:focus-visible { background: #8a340c; }
.fnt-msg { margin: 0 0 12px; padding: 8px 12px; border-radius: 8px; line-height: 1.5; }
.fnt-msg-ok { background: #eafaf0; color: #1b5e33; }
.fnt-msg-info { background: #f5f2ef; color: #4b4138; }
.fnt-msg-warn { background: #fff4e5; color: #7a4a00; }
.fnt-rule { border: 0; padding: 0; margin: 4px 0 14px; display: flex; flex-wrap: wrap; gap: 6px 18px; }
.fnt-rule legend { font-weight: 700; margin-bottom: 6px; color: #1f1712; }
.fnt-radio { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; }
.fnt-sub { font-weight: 700; text-transform: uppercase; font-size: 11px; letter-spacing: .08em; color: #a0958a; margin: 14px 0 4px; }
.fnt-muted { color: #8a7f75; margin: 0; }
.fnt-row { display: flex; align-items: center; gap: 12px; padding: 8px 0; border-bottom: 1px solid #fbf1e8; }
.fnt-row:last-of-type { border-bottom: none; }
.fnt-when { flex: 0 0 88px; font-size: 12.5px; font-weight: 700; color: #1f1712; display: flex; flex-direction: column; font-variant-numeric: tabular-nums; }
.fnt-when span { font-weight: 500; color: #8a7f75; }
.fnt-what { flex: 1; min-width: 0; }
.fnt-name { font-weight: 600; color: #1f1712; overflow-wrap: anywhere; }
.fnt-meta { font-size: 12.5px; color: #8a7f75; }
.fnt-err { color: #9c2b23; }
.fnt-toggle { flex: 0 0 auto; border: 1px solid #e9ddd2; background: #f5f2ef; color: #6f655c; border-radius: 16px; padding: 5px 12px; font-size: 12.5px; font-weight: 700; cursor: pointer; }
.fnt-toggle.on { background: #eafaf0; color: #1b7a3d; border-color: #b7e4c7; }
.fnt-toggle:disabled, .fnt-link:disabled { opacity: .55; cursor: default; }
.fnt-toggle:focus-visible, .fnt-link:focus-visible, .fnt-open:focus-visible { outline: 2px solid #a64010; outline-offset: 2px; }
.fnt-open { color: #a64010; font-weight: 700; white-space: nowrap; }
.fnt-link { margin-top: 14px; border: 0; background: none; padding: 0; color: #a64010; text-decoration: underline; cursor: pointer; font: inherit; }
@media (max-width: 480px) {
    .fnt { padding: 14px; }
    .fnt-row { flex-wrap: wrap; }
    .fnt-when { flex-basis: 100%; flex-direction: row; gap: 6px; }
    .fnt-what { flex-basis: calc(100% - 130px); }
}
</style>
